import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Refresh as RefreshIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import apiServiceCompany from '../../services/company.services';

const S = {
    body: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'linear-gradient(135deg, #d4f0ee 0%, #e8f7f6 50%, #cfeae8 100%)',
        color: '#1c2330',
        fontSize: 14,
        minHeight: '100vh',
    },
    layout: { padding: '24px 32px', maxWidth: 1380, margin: '0 auto' },
    main: { width: '100%' },
    backBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#0d8f85',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '.3px',
        textTransform: 'uppercase',
        padding: '6px 0',
        marginBottom: 8,
    },
    card: {
        background: '#fff',
        border: '1px solid #a8d8d4',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(13,143,133,.12)',
        overflow: 'hidden',
    },
    searchInputBox: { position: 'relative', display: 'flex', alignItems: 'center' },
    searchInput: {
        padding: '7px 30px 7px 32px',
        border: '1px solid #e2e5ea',
        borderRadius: 6,
        fontSize: 13,
        color: '#1c2330',
        outline: 'none',
        width: 240,
    },
    searchClear: {
        position: 'absolute',
        right: 8,
        cursor: 'pointer',
        color: '#9aa3ad',
        background: 'none',
        border: 'none',
        fontSize: 18,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
    },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
        padding: '16px',
        textAlign: 'left',
        fontSize: 14,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.6px',
        color: '#000000',
        borderBottom: '1px solid #a8d8d4',
        background: '#D6F3F3',
        whiteSpace: 'nowrap',
    },
    thInner: { display: 'inline-flex', alignItems: 'center', gap: 6 },
    tdName: {
        padding: '16px',
        verticalAlign: 'middle',
        fontWeight: 500,
        color: '#000000',
        width: '35%',
        fontSize: 14,
        fontFamily: 'monospace',
    },
    tdValue: { padding: '16px', verticalAlign: 'middle', width: '20%' },
    tdDesc: {
        padding: '16px',
        verticalAlign: 'middle',
        color: '#000000',
        lineHeight: 1.5,
        fontSize: 14,
        width: '45%',
    },
    valueSelect: {
        padding: '6px 30px 6px 11px',
        border: '1px solid #d1d5db',
        borderRadius: 6,
        fontSize: 13,
        color: '#374151',
        background: '#fff',
        cursor: 'pointer',
        outline: 'none',
        width: 130,
        appearance: 'none',
        WebkitAppearance: 'none',
    },
    rowModified: {
        background: '#FFFBEB',
        borderLeft: '3px solid #F59E0B',
    },
    rowDefault: {
        background: 'transparent',
        borderLeft: '3px solid transparent',
    },
    cardSectionTitle: {
        fontSize: 16,
        fontWeight: 700,
        color: '#0d8f85',
        letterSpacing: '.1px',
    },
    historyBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#0d8f85',
        fontSize: 13,
        fontWeight: 500,
        padding: '4px 8px',
        borderRadius: 4,
        textDecoration: 'none',
    },
    actionBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: '28px 18px 32px',
        background: '#f0f8f7',
    },
    btnReset: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 32px',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '.6px',
        textTransform: 'uppercase',
        cursor: 'pointer',
        background: '#fff',
        border: '1px solid #d1d5db',
        color: '#9aa3ad',
        minWidth: 160,
        justifyContent: 'center',
    },
    btnUpdate: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 48px',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '.6px',
        textTransform: 'uppercase',
        cursor: 'pointer',
        background: '#0d7a72',
        border: '1px solid #0d7a72',
        color: '#fff',
        minWidth: 180,
        justifyContent: 'center',
    },
    emptyCell: {
        textAlign: 'center',
        padding: '48px 18px',
        color: '#9aa3ad',
        fontSize: 14,
    },
    toast: (show) => ({
        position: 'fixed',
        bottom: 24,
        right: 24,
        background: '#1c2330',
        color: '#fff',
        padding: '10px 18px',
        borderRadius: 6,
        fontSize: 13,
        boxShadow: '0 4px 12px rgba(0,0,0,.25)',
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity .25s, transform .25s',
        pointerEvents: 'none',
        zIndex: 9999,
    }),
};

function useToast() {
    const [toast, setToast] = useState({ show: false, msg: '' });
    const timerRef = useRef(null);

    const showToast = (msg) => {
        clearTimeout(timerRef.current);
        setToast({ show: true, msg });
        timerRef.current = setTimeout(() => setToast({ show: false, msg: '' }), 2200);
    };

    return { toast, showToast };
}

function valueToApiString(v) {
    if (v === true || v === 'true') return 'true';
    if (v === false || v === 'false') return 'false';
    return 'notset';
}

export default function Overrides() {
    const navigate = useNavigate();
    const location = useLocation();

    // Company info passed from CompanyManagement via navigate state
    const selectedCompany = location.state?.company || null;

    const [overrides, setOverrides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingChanges, setPendingChanges] = useState([]);
    const [error, setError] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const { toast, showToast } = useToast();

    // Store the original values loaded from the API
    const initialValues = useRef({});

    // ── Fetch overrides from backend ──────────────────────────
    useEffect(() => {
        if (!selectedCompany?.id) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        apiServiceCompany
            .getCompanyOverrides(selectedCompany.id)
            .then((result) => {
                if (result.status === 'ok') {
                    const rows = result.data.overrides.map((o) => ({
                        name: o.key,
                        value: valueToApiString(o.value),
                        desc: o.description,
                    }));
                    setOverrides(rows);
                    initialValues.current = Object.fromEntries(
                        rows.map((o) => [o.name, o.value])
                    );
                } else {
                    setError('Failed to load overrides.');
                }
            })
            .catch(() => setError('Failed to load overrides.'))
            .finally(() => setLoading(false));
    }, [selectedCompany?.id]);

    // ── Filtering / Sorting / Pagination ─────────────────────
    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        return q
            ? overrides.filter(
                  (o) =>
                      o.name.toLowerCase().includes(q) ||
                      o.desc.toLowerCase().includes(q)
              )
            : [...overrides];
    }, [overrides, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * pageSize;
    const end = Math.min(start + pageSize, filtered.length);
    const pageRows = filtered.slice(start, end);
    const atFirst = safePage === 1;
    const atLast = safePage === totalPages;

    const goPage = (dir) =>
        setCurrentPage((p) => Math.min(Math.max(1, p + dir), totalPages));

    const handlePageSizeChange = (val) => {
        setPageSize(Number(val));
        setCurrentPage(1);
    };
    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };
    const clearSearch = () => {
        setSearch('');
        setCurrentPage(1);
    };
    const handleValueChange = (name, val) => {
        setOverrides((prev) =>
            prev.map((o) => (o.name === name ? { ...o, value: val } : o))
        );
    };

    const handleReset = () => {
        setOverrides((prev) =>
            prev.map((o) => ({
                ...o,
                value: initialValues.current[o.name] ?? 'notset',
            }))
        );
        showToast('All overrides reset to defaults');
    };

    const handleUpdateClick = () => {
        const changes = overrides.filter(
            (o) => o.value !== (initialValues.current[o.name] ?? 'notset')
        );
        if (changes.length === 0) {
            showToast('No changes to update.');
            return;
        }
        setPendingChanges(changes);
        setConfirmOpen(true);
    };

    const handleUpdate = async () => {
        if (!selectedCompany?.id) return;
        setSaving(true);
        try {
            // Build payload: only send keys that changed
            const payload = {};
            overrides.forEach((o) => {
                payload[o.name] = o.value;
            });
            const result = await apiServiceCompany.updateCompanyOverrides(
                selectedCompany.id,
                payload
            );
            if (result.status === 'ok') {
                // Refresh from server so initialValues stays in sync
                const refreshed = await apiServiceCompany.getCompanyOverrides(
                    selectedCompany.id
                );
                if (refreshed.status === 'ok') {
                    const rows = refreshed.data.overrides.map((o) => ({
                        name: o.key,
                        value: valueToApiString(o.value),
                        desc: o.description,
                    }));
                    setOverrides(rows);
                    initialValues.current = Object.fromEntries(
                        rows.map((o) => [o.name, o.value])
                    );
                }
                showToast('Overrides updated successfully');
            } else {
                showToast('Update failed. Please try again.');
            }
        } catch {
            showToast('Update failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleRefresh = () => {
        if (!selectedCompany?.id) return;
        setLoading(true);
        setError(null);
        apiServiceCompany
            .getCompanyOverrides(selectedCompany.id)
            .then((result) => {
                if (result.status === 'ok') {
                    const rows = result.data.overrides.map((o) => ({
                        name: o.key,
                        value: valueToApiString(o.value),
                        desc: o.description,
                    }));
                    setOverrides(rows);
                    initialValues.current = Object.fromEntries(
                        rows.map((o) => [o.name, o.value])
                    );
                    showToast('Refreshed');
                } else {
                    setError('Failed to load overrides.');
                }
            })
            .catch(() => setError('Failed to load overrides.'))
            .finally(() => setLoading(false));
    };

    const navigateToOverrideHistory = () => {
        if (!selectedCompany?.id) return;
        navigate('/companies/history', {
            state: {
                companyId: selectedCompany.id,
                companyName: selectedCompany.name,
                opportunityId: selectedCompany.opportunityId || null,
                country: selectedCompany.country || null,
                buddyAccount: selectedCompany.buddyAccount || null,
                customerId: selectedCompany.customerId || null,
                crmInstanceId: selectedCompany.crmInstanceId || null,
                entitlementCount: selectedCompany.entitlementCount ?? 0,
                overrideCount: selectedCompany.overrideCount ?? 0,
                historyType: 'OVERRIDE',
            },
        });
    };

    // ── Select styles ─────────────────────────────────────────
    const chevronBg =
        'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\' viewBox=\'0 0 10 6\'%3E%3Cpath fill=\'%239aa3ad\' d=\'M0 0l5 6 5-6z\'/%3E%3C/svg%3E")';

    const selectStyle = {
        padding: '6px 28px 6px 10px',
        border: '1px solid #e2e5ea',
        borderRadius: 6,
        fontSize: 13,
        color: '#1c2330',
        background: '#fff',
        cursor: 'pointer',
        outline: 'none',
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundImage: chevronBg,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 9px center',
    };

    // ── Guard: no company selected ────────────────────────────
    if (!selectedCompany) {
        return (
            <div style={S.body}>
                <div style={{ height: 3, background: '#0d8f85' }} />
                <div style={S.layout}>
                    <button
                        style={S.backBtn}
                        onClick={() => navigate('/company-management')}
                    >
                        <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                            <path
                                d='M8.5 2L4.5 6L8.5 10'
                                stroke='currentColor'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                            />
                        </svg>
                        Back
                    </button>
                    <div
                        style={{
                            marginTop: 40,
                            textAlign: 'center',
                            color: '#9aa3ad',
                            fontSize: 15,
                        }}
                    >
                        No company selected. Please go back and select a company first.
                    </div>
                </div>
            </div>
        );
    }

    // ── Loading state ─────────────────────────────────────────
    if (loading) {
        return (
            <div style={S.body}>
                <div style={{ height: 3, background: '#0d8f85' }} />
                <div style={S.layout}>
                    <button
                        style={S.backBtn}
                        onClick={() =>
                            navigate('/company-management', {
                                state: { company: selectedCompany, activeTab: 'overrides' },
                            })
                        }
                    >
                        <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                            <path
                                d='M8.5 2L4.5 6L8.5 10'
                                stroke='currentColor'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                            />
                        </svg>
                        Back
                    </button>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50vh',
                        }}
                    >
                        <CircularProgress sx={{ color: '#0d8f85' }} />
                    </Box>
                </div>
            </div>
        );
    }

    return (
        <div style={S.body}>
            <div style={{ height: 3, background: '#0d8f85' }} />

            <div style={S.layout}>
                <div style={S.main}>
                    {/* ── Back button ── */}
                    <button
                        style={S.backBtn}
                        onClick={() => {
                            const trueCount = Object.values(initialValues.current).filter(v => v === 'true').length;
                            navigate('/company-management', {
                                state: {
                                    company: { ...selectedCompany, overrideCount: trueCount },
                                    activeTab: 'overrides',
                                },
                            });
                        }}
                    >
                        <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                            <path
                                d='M8.5 2L4.5 6L8.5 10'
                                stroke='currentColor'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                            />
                        </svg>
                        Back
                    </button>

                    {/* ── Company info banner ── */}
                    <div
                        style={{
                            background: '#fff',
                            border: '1px solid #0d8f85',
                            borderRadius: 8,
                            padding: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 22,
                            boxShadow: '0 2px 4px rgba(0,0,0,.05)',
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#0d8f85',
                                    textTransform: 'uppercase',
                                    letterSpacing: '.5px',
                                    marginBottom: 8,
                                }}
                            >
                                CURRENT COMPANY
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <span
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: '#000',
                                    }}
                                >
                                    {selectedCompany.name}
                                </span>
                                <span
                                    style={{
                                        fontSize: 14,
                                        color: '#000',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    ID: {selectedCompany.id}
                                </span>
                                <button
                                    title='Copy ID'
                                    onClick={() => {
                                        navigator.clipboard?.writeText(
                                            selectedCompany.id || ''
                                        );
                                        showToast('ID copied');
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 2,
                                        color: '#0d8f85',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <svg width='15' height='15' viewBox='0 0 14 14' fill='none'>
                                        <rect
                                            x='4'
                                            y='4'
                                            width='8'
                                            height='8'
                                            rx='1.5'
                                            stroke='#0d8f85'
                                            strokeWidth='1.3'
                                        />
                                        <path
                                            d='M2 10V3a1 1 0 011-1h7'
                                            stroke='#0d8f85'
                                            strokeWidth='1.3'
                                            strokeLinecap='round'
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div style={{ marginLeft: 'auto' }}>
                            <button
                                style={S.historyBtn}
                                onClick={navigateToOverrideHistory}
                                title='View override history in Entitlement History'
                            >
                                <svg
                                    width='14'
                                    height='14'
                                    viewBox='0 0 14 14'
                                    fill='none'
                                >
                                    <circle
                                        cx='7'
                                        cy='7'
                                        r='5.5'
                                        stroke='#0d8f85'
                                        strokeWidth='1.3'
                                    />
                                    <path
                                        d='M7 4v3.2l2 1.4'
                                        stroke='#0d8f85'
                                        strokeWidth='1.3'
                                        strokeLinecap='round'
                                    />
                                </svg>
                                Override History
                            </button>
                        </div>
                    </div>

                    {/* ── Error banner ── */}
                    {error && (
                        <div
                            style={{
                                marginBottom: 18,
                                padding: '10px 16px',
                                background: '#fcebeb',
                                border: '1px solid #f8c8c8',
                                borderRadius: 6,
                                color: '#a32d2d',
                                fontSize: 13,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    {/* ── Overrides table card ── */}
                    <div style={S.card}>
                        {/* Toolbar */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '12px 18px',
                                borderBottom: '1px solid #e2e5ea',
                                flexWrap: 'wrap',
                            }}
                        >
                            <span style={S.cardSectionTitle}>Overrides</span>

                            <div
                                style={{
                                    marginLeft: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                {/* Search */}
                                <div style={S.searchInputBox}>
                                    <svg
                                        width='14'
                                        height='14'
                                        viewBox='0 0 15 15'
                                        fill='none'
                                        style={{
                                            position: 'absolute',
                                            left: 9,
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <circle
                                            cx='6.5'
                                            cy='6.5'
                                            r='5'
                                            stroke='#9aa3ad'
                                            strokeWidth='1.5'
                                        />
                                        <path
                                            d='M10.5 10.5L13.5 13.5'
                                            stroke='#9aa3ad'
                                            strokeWidth='1.5'
                                            strokeLinecap='round'
                                        />
                                    </svg>
                                    <input
                                        type='text'
                                        value={search}
                                        onChange={handleSearch}
                                        placeholder='Filter overrides...'
                                        style={{ ...S.searchInput, width: 200 }}
                                    />
                                    {search && (
                                        <button
                                            onClick={clearSearch}
                                            style={S.searchClear}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>

                                {/* Refresh */}
                                <button
                                    onClick={handleRefresh}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        padding: '6px 12px',
                                        borderRadius: 6,
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        background: '#fff',
                                        border: '1px solid #d1d5db',
                                        color: '#374151',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <RefreshIcon sx={{ fontSize: 16 }} />
                                    REFRESH
                                </button>

                                {/* Page size */}
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        fontSize: 13,
                                        color: '#5a6473',
                                    }}
                                >
                                    <span>Page Size:</span>
                                    <select
                                        value={pageSize}
                                        onChange={(e) =>
                                            handlePageSizeChange(e.target.value)
                                        }
                                        style={selectStyle}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>

                                {/* Range */}
                                <span
                                    style={{
                                        fontSize: 13,
                                        color: '#5a6473',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {filtered.length === 0
                                        ? '0 of 0'
                                        : `${start + 1}–${end} of ${filtered.length}`}
                                </span>

                                {/* Prev */}
                                <button
                                    disabled={atFirst}
                                    onClick={() => goPage(-1)}
                                    style={{
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #d1d5db',
                                        borderRadius: 4,
                                        background: '#fff',
                                        cursor: atFirst ? 'not-allowed' : 'pointer',
                                        opacity: atFirst ? 0.4 : 1,
                                        color: '#374151',
                                    }}
                                >
                                    <svg
                                        width='8'
                                        height='13'
                                        viewBox='0 0 8 13'
                                        fill='none'
                                    >
                                        <path
                                            d='M6.5 1.5L1.5 6.5L6.5 11.5'
                                            stroke='currentColor'
                                            strokeWidth='1.5'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                    </svg>
                                </button>

                                {/* Page indicator */}
                                <span
                                    style={{
                                        fontSize: 13,
                                        color: '#5a6473',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Page {safePage} of {totalPages}
                                </span>

                                {/* Next */}
                                <button
                                    disabled={atLast}
                                    onClick={() => goPage(1)}
                                    style={{
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #d1d5db',
                                        borderRadius: 4,
                                        background: '#fff',
                                        cursor: atLast ? 'not-allowed' : 'pointer',
                                        opacity: atLast ? 0.4 : 1,
                                        color: '#374151',
                                    }}
                                >
                                    <svg
                                        width='8'
                                        height='13'
                                        viewBox='0 0 8 13'
                                        fill='none'
                                    >
                                        <path
                                            d='M1.5 1.5L6.5 6.5L1.5 11.5'
                                            stroke='currentColor'
                                            strokeWidth='1.5'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Table */}
                        <div style={S.tableWrap}>
                            <table style={S.table}>
                                <thead>
                                    <tr>
                                        <th style={S.th}>Name</th>
                                        <th style={S.th}>Value</th>
                                        <th style={S.th}>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={3} style={S.emptyCell}>
                                                {error
                                                    ? 'Error loading overrides.'
                                                    : 'No overrides match your search.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        pageRows.map((o) => {
                                            const isModified =
                                                o.value !==
                                                (initialValues.current[o.name] ??
                                                    'notset');
                                            return (
                                                <tr
                                                    key={o.name}
                                                    style={{
                                                        borderBottom:
                                                            '1px solid #e2e5ea',
                                                        ...(isModified
                                                            ? S.rowModified
                                                            : S.rowDefault),
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isModified)
                                                            e.currentTarget.style.background = '#E8F8F8';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = isModified ? '#FFFBEB' : 'transparent';
                                                    }}
                                                >
                                                    <td style={S.tdName}>
                                                        {o.name}
                                                    </td>
                                                    <td style={S.tdValue}>
                                                        <Select
                                                            size='small'
                                                            value={o.value}
                                                            onChange={(e) =>
                                                                handleValueChange(
                                                                    o.name,
                                                                    e.target.value
                                                                )
                                                            }
                                                            sx={{
                                                                fontSize: 14,
                                                                width: 120,
                                                                minWidth: 120,
                                                                height: 40,
                                                                borderRadius: 1.5,
                                                                color: '#000000',
                                                            }}
                                                        >
                                                            <MenuItem value='notset' sx={{ fontSize: 14, color: '#000000' }}>Not Set</MenuItem>
                                                            <MenuItem value='true' sx={{ fontSize: 14, color: '#000000' }}>true</MenuItem>
                                                            <MenuItem value='false' sx={{ fontSize: 14, color: '#000000' }}>false</MenuItem>
                                                        </Select>
                                                    </td>
                                                    <td style={S.tdDesc}>
                                                        {o.desc}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Override History panel ── */}
                    {showHistory && (
                        <div
                            style={{
                                marginTop: 20,
                                background: '#fff',
                                border: '1px solid #e2e5ea',
                                borderRadius: 6,
                                padding: '18px 22px',
                                boxShadow: '0 1px 4px rgba(0,0,0,.08)',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 14,
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight: 700,
                                        fontSize: 15,
                                        color: '#0d8f85',
                                    }}
                                >
                                    Override History
                                </span>
                                <button
                                    onClick={() => setShowHistory(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#9aa3ad',
                                        fontSize: 18,
                                        lineHeight: 1,
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                            <p
                                style={{
                                    color: '#9aa3ad',
                                    fontSize: 13,
                                    margin: 0,
                                }}
                            >
                                No override history available for this company.
                            </p>
                        </div>
                    )}

                    {/* ── Action bar ── */}
                    <div style={S.actionBar}>
                        <button
                            style={S.btnReset}
                            onClick={handleReset}
                            disabled={saving}
                        >
                            <ResetIcon sx={{ fontSize: 18, color: '#9aa3ad' }} />
                            Reset
                        </button>
                        <button
                            style={{
                                ...S.btnUpdate,
                                opacity: saving ? 0.7 : 1,
                                cursor: saving ? 'not-allowed' : 'pointer',
                            }}
                            onClick={handleUpdateClick}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Update'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Confirm Update Dialog ── */}
            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth='sm'
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#0d7a72' }}>
                    Confirm Override Changes
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText sx={{ mb: 2, color: '#374151' }}>
                        The following overrides will be updated for <strong>{selectedCompany?.name}</strong>. Are you sure?
                    </DialogContentText>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#D6F3F3' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700 }}>Override</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700 }}>From</th>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700 }}>To</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingChanges.map((o, i) => (
                                <tr key={o.name} style={{ background: i % 2 === 0 ? '#f9fafb' : '#fff' }}>
                                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{o.name}</td>
                                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                        {initialValues.current[o.name] ?? 'notset'}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 600,
                                        color: o.value === 'true' ? '#0d7a72' : o.value === 'false' ? '#b91c1c' : '#6b7280'
                                    }}>
                                        {o.value}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={saving}
                        sx={{ color: '#6b7280' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => { setConfirmOpen(false); handleUpdate(); }}
                        variant='contained'
                        disabled={saving}
                        sx={{ background: '#0d7a72', '&:hover': { background: '#0a6560' } }}
                    >
                        {saving ? 'Saving…' : 'Confirm Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Toast ── */}
            <div style={S.toast(toast.show)}>{toast.msg}</div>
        </div>
    );
}
